import { useQuery } from "@tanstack/react-query";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Building2, Mail, Phone, FileText, FolderOpen, AlertTriangle, ListChecks } from "lucide-react";
import { buscarCliente } from "../../services/clientes";
import { ProjetoAvatar } from "../../components/ProjetoAvatar";

const STATUS_LABELS: Record<string, string> = {
  planejamento: "Planejamento",
  ativo: "Ativo",
  em_validacao: "Em validação",
  entregue: "Entregue",
  concluido: "Concluído",
  pausado: "Pausado",
};
const STATUS_COR: Record<string, string> = {
  planejamento: "bg-slate-100 text-slate-600",
  ativo: "bg-blue-100 text-blue-700",
  em_validacao: "bg-amber-100 text-amber-700",
  entregue: "bg-emerald-100 text-emerald-700",
  concluido: "bg-slate-200 text-slate-600",
  pausado: "bg-orange-100 text-orange-700",
};

function ResumoCard({ valor, label, cor }: { valor: number; label: string; cor: string }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4">
      <p className={`text-2xl font-bold ${cor}`}>{valor}</p>
      <p className="text-xs text-slate-500 mt-0.5">{label}</p>
    </div>
  );
}

export function ClienteDetalhe() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: cliente, isLoading, isError } = useQuery({
    queryKey: ["cliente", id],
    queryFn: () => buscarCliente(id!),
    enabled: !!id,
  });

  if (isLoading) return <div className="text-slate-400">Carregando…</div>;
  if (isError || !cliente) return <div className="text-slate-500">Cliente não encontrado.</div>;

  const linkAssetsOk = cliente.linkAssets && /^https?:\/\//i.test(cliente.linkAssets);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start gap-4">
        <button onClick={() => navigate("/clientes")} className="p-2 rounded-lg hover:bg-slate-100 transition-colors mt-1">
          <ArrowLeft size={20} className="text-slate-500" />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <Building2 size={22} className="text-slate-400" /> {cliente.nome}
            </h1>
            {cliente.codigo && <span className="font-mono text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded">{cliente.codigo}</span>}
            <span className={`text-xs font-medium px-2 py-0.5 rounded ${cliente.ativo ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
              {cliente.ativo ? "Ativo" : "Inativo"}
            </span>
          </div>
          <div className="flex items-center gap-4 mt-1.5 text-sm text-slate-500 flex-wrap">
            {cliente.email && <span className="flex items-center gap-1"><Mail size={13} /> {cliente.email}</span>}
            {cliente.telefone && <span className="flex items-center gap-1"><Phone size={13} /> {cliente.telefone}</span>}
            {cliente.documento && <span className="flex items-center gap-1"><FileText size={13} /> {cliente.documento}</span>}
            {linkAssetsOk && (
              <a href={cliente.linkAssets!} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-blue-600 hover:text-blue-700 underline">
                <FolderOpen size={13} /> Pasta de assets
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Resumo */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <ResumoCard valor={cliente.resumo.projetosAtivos} label="Projetos ativos" cor="text-blue-600" />
        <ResumoCard valor={cliente.resumo.projetosFinalizados} label="Finalizados" cor="text-emerald-600" />
        <ResumoCard valor={cliente.resumo.tarefasAbertas} label="Tarefas abertas" cor="text-slate-700" />
        <ResumoCard valor={cliente.resumo.tarefasAtrasadas} label="Tarefas atrasadas" cor={cliente.resumo.tarefasAtrasadas > 0 ? "text-red-600" : "text-slate-700"} />
      </div>

      {/* Projetos */}
      <div>
        <h2 className="text-sm font-semibold uppercase text-slate-500 mb-2">Projetos ({cliente.projetos.length})</h2>
        {cliente.projetos.length === 0 ? (
          <p className="text-sm text-slate-400">Nenhum projeto vinculado.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {cliente.projetos.map((p) => (
              <Link
                key={p.id}
                to={`/projetos/${p.id}`}
                className="bg-white rounded-xl border border-slate-200 p-4 hover:shadow-md hover:border-blue-300 transition-all"
              >
                <div className="flex items-center gap-2">
                  <ProjetoAvatar nome={p.nome} fotoUrl={p.fotoUrl} cor="#64748b" tamanho="sm" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-800 truncate">{p.nome}</p>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded ${STATUS_COR[p.status] || "bg-slate-100 text-slate-600"}`}>
                        {STATUS_LABELS[p.status] || p.status}
                      </span>
                      {p.modalidade && <span className="text-[10px] text-slate-400 uppercase">{p.modalidade}</span>}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3 mt-3 text-xs text-slate-500">
                  <span className="flex items-center gap-1"><ListChecks size={12} /> {p.tarefasAbertas}/{p.tarefasTotal} abertas</span>
                  {p.tarefasAtrasadas > 0 && (
                    <span className="flex items-center gap-1 text-red-600 font-medium"><AlertTriangle size={12} /> {p.tarefasAtrasadas} atrasada{p.tarefasAtrasadas > 1 ? "s" : ""}</span>
                  )}
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
